/**
 * 浏览器语言检测工具
 * 自动检测用户首选语言并映射到支持的语言
 */

import type { LanguageCode, ActiveLanguage } from './types';

// 浏览器语言到应用语言的映射
const BROWSER_LANG_MAP: Record<string, LanguageCode> = {
  // 中文变体
  'zh': 'zh-CN',
  'zh-CN': 'zh-CN',
  'zh-TW': 'zh-CN',
  'zh-HK': 'zh-CN',
  'zh-MO': 'zh-CN',
  'zh-SG': 'zh-CN',
  // 日语
  'ja': 'ja',
  'jp': 'ja', // 常见的错误代码
  // 英语
  'en': 'en',
  'en-US': 'en',
  'en-GB': 'en',
  'en-CA': 'en',
  'en-AU': 'en',
  // 阿拉伯语（预留）
  'ar': 'ar',
  'ar-SA': 'ar',
  // 西班牙语（预留）
  'es': 'es',
  'es-ES': 'es',
  'es-MX': 'es',
  // 韩语（预留）
  'ko': 'ko',
  'ko-KR': 'ko',
  // 葡萄牙语（预留）
  'pt': 'pt-BR',
  'pt-BR': 'pt-BR',
  'pt-PT': 'pt-BR',
};

// 默认语言
const DEFAULT_LANG: ActiveLanguage = 'en';

// 已激活的语言列表
const ACTIVE_LANGS: ActiveLanguage[] = ['en', 'zh-CN', 'ja'];

/**
 * 将浏览器语言代码映射到应用语言
 */
export function mapBrowserLanguage(browserLang: string): ActiveLanguage {
  // 直接匹配
  if (BROWSER_LANG_MAP[browserLang]) {
    const mapped = BROWSER_LANG_MAP[browserLang];
    if (ACTIVE_LANGS.includes(mapped as ActiveLanguage)) {
      return mapped as ActiveLanguage;
    }
  }
  
  // 尝试匹配主语言代码（去掉地区后缀）
  const primaryLang = browserLang.split('-')[0];
  if (BROWSER_LANG_MAP[primaryLang]) {
    const mapped = BROWSER_LANG_MAP[primaryLang];
    if (ACTIVE_LANGS.includes(mapped as ActiveLanguage)) {
      return mapped as ActiveLanguage;
    }
  }
  
  return DEFAULT_LANG;
}

/**
 * 检测浏览器首选语言
 */
export function detectBrowserLanguage(): ActiveLanguage {
  if (typeof navigator === 'undefined') return DEFAULT_LANG;
  
  // 1. 优先使用 navigator.languages（按优先级排序的语言列表）
  if (navigator.languages && navigator.languages.length > 0) {
    for (const lang of navigator.languages) {
      const mapped = mapBrowserLanguage(lang);
      if (mapped !== DEFAULT_LANG) return mapped;
    }
  }
  
  // 2. 回退到 navigator.language
  if (navigator.language) {
    return mapBrowserLanguage(navigator.language);
  }
  
  return DEFAULT_LANG;
}

/**
 * 验证语言代码是否有效且已激活
 */
export function validateLanguage(code: string): ActiveLanguage {
  if (ACTIVE_LANGS.includes(code as ActiveLanguage)) {
    return code as ActiveLanguage;
  }
  return DEFAULT_LANG;
}

/**
 * 从 URL 路径中提取语言代码
 * 例如: /zh-CN/agent → zh-CN
 */
export function extractLanguageFromPath(path: string): ActiveLanguage | null {
  const match = path.match(/^\/(en|zh-CN|ja)(?:\/|$)/);
  if (match) {
    return validateLanguage(match[1]);
  }
  return null;
}

/**
 * 构建带语言前缀的路径
 */
export function buildLocalizedPath(
  lang: LanguageCode, 
  path: string = ''
): string {
  // 移除已有的语言前缀
  const cleanPath = path.replace(/^\/(en|zh-CN|ja)(?:\/|$)/, '/');
  // 构建新路径
  const normalizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  return `/${lang}${normalizedPath === '/' ? '' : normalizedPath}`;
}
